import React, { useState, useEffect, useRef } from 'react';
import { Icon, Input, Tabs, message, Button, Tooltip } from 'antd';
import { withTranslation } from 'react-i18next';
import {
  EditorContainer,
  LeftPanel,
  RightPanel,
  BackLink,
  EditorTitle,
  EditorDescription,
  FormField,
  TipCard,
  TabsContainer,
  TabHeader,
  ImportButton,
  SaveButton,
  JsonValidationStatus
} from './styles';
import JsonMappingEditor from './JsonMappingEditor';
import XPathMappingsEditor from './XPathMappingsEditor';
import { getContract, createContract, updateContract } from '../../utils/services/contractsAPI';
import { 
  extractJsonSchema, 
  extractXmlSchema, 
  validateJson,
  buildVariableMaps,
  convertStorageToDisplay,
  convertDisplayToStorage,
  convertLogicMappingStorageToDisplayEntries,
  convertLogicMappingDisplayEntriesToStorage
} from '../../utils/contractHelpers';
import { getAllVariables, getAllVariableCategory, saveVariable } from '../../utils/services/variablesAPI';
import { BUSINESS_CONTRACTS as trans } from './translationValues';

const { TextArea } = Input;
const { TabPane } = Tabs;

const NAMESPACES = ['BUSINESS_CONTRACTS'];

const ContractEditor = props => {
  const { t, contractId, onClose } = props;
  const id = contractId;
  const isNew = !id || id === null;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dataMapping, setDataMapping] = useState({});
  const [logicMapping, setLogicMapping] = useState([]);
  const [hasSchema, setHasSchema] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [activeTab, setActiveTab] = useState('json');
  const [variableMaps, setVariableMaps] = useState({ placeholderToId: new Map(), idToPlaceholder: new Map() });
  const [globalVariables, setGlobalVariables] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [jsonValidationError, setJsonValidationError] = useState(null);
  const fileInputRef = useRef(null);
  const hasConvertedRef = useRef(false);
  const pendingLogicStorageRef = useRef(null);
  const jsonEditorData = useRef(null);

  // Fetch variables and categories on mount
  useEffect(() => {
    const fetchVariablesAndCategories = async () => {
      try {
        const [varsResponse, catsResponse] = await Promise.all([
          getAllVariables(),
          getAllVariableCategory()
        ]);

        const vars = (varsResponse && varsResponse.statusCode === 1217) 
          ? varsResponse.data 
          : (varsResponse?.data || []);
        const cats = catsResponse?.data || [];

        // Build category map
        const categoryMap = {};
        cats.forEach(cat => {
          categoryMap[cat.id] = cat.name;
        });

        // Format variables with variableCategoryName (required by VariableMentionPopup)
        const formattedVars = vars.map(v => ({
          ...v,
          variableCategoryName: v.variableCategoryName || categoryMap[v.variableCategoryId] || ''
        }));

        setGlobalVariables(formattedVars);
        setRelationships([]); // Relationships can be added later if needed

        const maps = buildVariableMaps(vars, cats);
        setVariableMaps(maps);
      } catch (error) {
        console.error('Error fetching variables and categories:', error);
      }
    };

    fetchVariablesAndCategories();
  }, []);

  useEffect(() => {
    if (!isNew) {
      fetchContract();
    }
  }, [id]);

  const fetchContract = async () => {
    setLoading(true);
    try {
      const response = await getContract(id);
      if (response && response.statusCode === 6006 && response.data) {
        const contract = response.data;
        setTitle(contract.name || '');
        setDescription(contract.description || '');
        
        // Extract mapping data from nested structure
        const mapping = contract.mapping || {};
        const storageDataMapping = mapping.data_mapping || contract.data_mapping || contract.dataMapping || {};
        const storageLogicMapping = mapping.logic_mapping || contract.logic_mapping || contract.logicMapping || {};
        
        // Store raw data - conversion will happen when variable maps are ready
        setDataMapping(storageDataMapping);
        jsonEditorData.current = JSON.stringify(storageDataMapping);
        pendingLogicStorageRef.current = storageLogicMapping;
        setLogicMapping([]);
        setHasSchema(Object.keys(storageDataMapping).length > 0);
      } else {
        message.error('Error loading contract');
        if (onClose) onClose();
      }
    } catch (error) {
      console.error('Error fetching contract:', error);
      message.error('Error loading contract');
      if (onClose) onClose();
    } finally {
      setLoading(false);
    }
  };

  // Convert data when variable maps are ready and we have contract data
  useEffect(() => {
    if (!isNew && variableMaps.idToPlaceholder.size > 0 && !hasConvertedRef.current) {
      // Check if dataMapping contains template strings (storage format)
      const displayDataMapping = convertStorageToDisplay(dataMapping, variableMaps.idToPlaceholder);
      const displayLogicEntries = convertLogicMappingStorageToDisplayEntries(
        pendingLogicStorageRef.current || {},
        variableMaps.idToPlaceholder
      );
      setDataMapping(displayDataMapping);
      jsonEditorData.current = JSON.stringify(displayDataMapping);
      setLogicMapping(displayLogicEntries);
      hasConvertedRef.current = true;
    }
  }, [variableMaps, isNew, dataMapping, logicMapping]);
  
  // Reset conversion flag when switching contracts
  useEffect(() => {
    hasConvertedRef.current = false;
  }, [id]);

  // Handle updateGlobalVariables callback for VariableMentionPopup
  const handleUpdateGlobalVariables = async (newVariable, isLocal = false) => {
    if (isLocal) {
      // For local variables, just add to the list
      const newList = [newVariable, ...globalVariables];
      setGlobalVariables(newList);
      return [newVariable, newList];
    }

    try {
      const response = await saveVariable(newVariable);
      if (response && response.errors && response.errors.length === 0) {
        const newVar = { ...newVariable, id: response.id };
        const newList = [newVar, ...globalVariables];
        setGlobalVariables(newList);

        // Update variable maps
        if (newVar.placeHolderText) {
          const placeholderText = `${newVar.placeHolderText}`;
          const updatedPlaceholderToId = new Map(variableMaps.placeholderToId);
          const updatedIdToPlaceholder = new Map(variableMaps.idToPlaceholder);
          updatedPlaceholderToId.set(placeholderText, newVar.id);
          updatedIdToPlaceholder.set(newVar.id, placeholderText);
          setVariableMaps({
            placeholderToId: updatedPlaceholderToId,
            idToPlaceholder: updatedIdToPlaceholder
          });
        }

        return [newVar, newList];
      } else {
        throw new Error('Failed to save variable');
      }
    } catch (error) {
      console.error('Error saving variable:', error);
      throw error;
    }
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    if (
      fileType !== 'application/json' &&
      fileType !== 'text/json' &&
      !fileName.endsWith('.json') &&
      fileType !== 'application/xml' &&
      fileType !== 'text/xml' &&
      !fileName.endsWith('.xml')
    ) {
      message.error(t(trans.INVALID_FILE_TYPE.key, trans.INVALID_FILE_TYPE.value));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        let schema = {};

        if (fileName.endsWith('.json') || fileType === 'application/json' || fileType === 'text/json') {
          const jsonData = JSON.parse(content);
          schema = extractJsonSchema(jsonData);
        } else if (fileName.endsWith('.xml') || fileType === 'application/xml' || fileType === 'text/xml') {
          schema = extractXmlSchema(content);
        }

        if (Object.keys(schema).length > 0) {
          jsonEditorData.current = JSON.stringify(schema, null, 2);
          setDataMapping(schema);
          setHasSchema(true);
          message.success('File imported successfully');
        } else {
          message.error(t(trans.ERROR_PARSING_FILE.key, trans.ERROR_PARSING_FILE.value));
        }
      } catch (error) {
        console.error('Error parsing file:', error);
        message.error(t(trans.ERROR_PARSING_FILE.key, trans.ERROR_PARSING_FILE.value));
      }
      // Clear the file input using ref instead of event target
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    if (fileName.endsWith('.json') || fileType === 'application/json' || fileType === 'text/json') {
      reader.readAsText(file);
    } else {
      reader.readAsText(file);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      message.error(t(trans.REQUIRED_FIELD.key, trans.REQUIRED_FIELD.value));
      return;
    }

    // Validate JSON mapping only on save
    try {
      const jsonString = JSON.stringify(dataMapping, null, 2);
      const validation = validateJson(jsonString);
      if (!validation.valid) {
        setJsonValidationError(validation.error);
        message.error(`Invalid JSON mapping: ${validation.error || 'Please fix errors before saving.'}`);
        return;
      }
      setJsonValidationError(null);
    } catch (error) {
      setJsonValidationError(error.message);
      message.error(`Invalid JSON mapping: ${error.message || 'Please fix errors before saving.'}`);
      return;
    }

    setSaving(true);
    try {
      // Convert from display format to storage format
      const storageDataMapping = convertDisplayToStorage(dataMapping, variableMaps.placeholderToId);
      const storageLogicMapping = convertLogicMappingDisplayEntriesToStorage(
        logicMapping,
        variableMaps.placeholderToId
      );

      const payload = {
        name: title.trim(),
        description: description.trim(),
        mapping: {
          data_mapping: storageDataMapping,
          logic_mapping: storageLogicMapping
        }
      };

      let response;
      if (isNew) {
        response = await createContract(payload);
      } else {
        response = await updateContract(id, payload);
      }

      if (response && (response.statusCode === 6004 || response.statusCode === 6002)) {
        message.success(t(trans.CONTRACT_SAVED.key, trans.CONTRACT_SAVED.value), 0.5);
        // if (onClose) onClose();
      } else {
        message.error(t(trans.ERROR_SAVING_CONTRACT.key, trans.ERROR_SAVING_CONTRACT.value));
      }
    } catch (error) {
      console.error('Error saving contract:', error);
      message.error(t(trans.ERROR_SAVING_CONTRACT.key, trans.ERROR_SAVING_CONTRACT.value));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <EditorContainer>
      <LeftPanel>
        <BackLink onClick={() => onClose && onClose()}>
          <Icon type="arrow-left" />
          {t(trans.BACK_TO_CONTRACTS.key, trans.BACK_TO_CONTRACTS.value)}
        </BackLink>
        <EditorTitle>
          {isNew ? t(trans.NEW_CONTRACT.key, trans.NEW_CONTRACT.value) : t(trans.EDIT_CONTRACT.key, trans.EDIT_CONTRACT.value)}
        </EditorTitle>
        <EditorDescription>
          {t(trans.NEW_CONTRACT_DESCRIPTION.key, trans.NEW_CONTRACT_DESCRIPTION.value)}
        </EditorDescription>

        <FormField>
          <label>{t(trans.CONTRACT_TITLE_REQUIRED.key, trans.CONTRACT_TITLE_REQUIRED.value)}</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t(trans.CONTRACT_TITLE_PLACEHOLDER.key, trans.CONTRACT_TITLE_PLACEHOLDER.value)}
          />
        </FormField>

        <FormField>
          <label>{t(trans.DESCRIPTION.key, trans.DESCRIPTION.value)}</label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t(trans.DESCRIPTION_PLACEHOLDER.key, trans.DESCRIPTION_PLACEHOLDER.value)}
            rows={4}
          />
        </FormField>

        <TipCard>
          <h4>{t(trans.QUICK_TIP_TITLE.key, trans.QUICK_TIP_TITLE.value)}</h4>
          <p>
            {t(trans.QUICK_TIP_CONTENT.key, trans.QUICK_TIP_CONTENT.value).split('Entity.attribute').map((part, index) => (
              <React.Fragment key={index}>
                {part}
                {index === 0 && <span className="entity-attribute">Entity.attribute</span>}
              </React.Fragment>
            ))}
          </p>
        </TipCard>

        <TipCard>
          <h4>{t(trans.COMPLEX_LOGIC_TITLE.key, trans.COMPLEX_LOGIC_TITLE.value)}</h4>
          <p>{t(trans.COMPLEX_LOGIC_CONTENT.key, trans.COMPLEX_LOGIC_CONTENT.value)}</p>
        </TipCard>
      </LeftPanel>

      <RightPanel>
        <TabHeader>
          <div style={{ flex: 1 }}>
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab} 
              style={{ width: '100%' }}
              tabBarStyle={{ marginBottom: 0 }}
            >
              <TabPane 
                tab={t(trans.JSON_MAPPING.key, trans.JSON_MAPPING.value) || 'JSON Mapping'} 
                key="json"
              />
              <TabPane 
                key="xpath"
                tab={
                  <span>
                    {t(trans.COMPLEX_LOGIC_MAPPINGS.key, trans.COMPLEX_LOGIC_MAPPINGS.value) || 'Complex Logic Mappings'}
                    {Array.isArray(logicMapping) && logicMapping.length > 0 && (
                      <span style={{
                        marginLeft: '8px',
                        background: '#7c3aed',
                        color: '#fff',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {logicMapping.length}
                      </span>
                    )}
                  </span>
                }
              />
            </Tabs>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,.xml,application/json,application/xml,text/xml"
            onChange={handleFileImport}
            style={{ display: 'none' }}
            id="file-import-input"
          />
          <ImportButton
            icon="upload"
            onClick={() => document.getElementById('file-import-input').click()}
          >
            {t(trans.IMPORT_JSON_XML.key, trans.IMPORT_JSON_XML.value)}
          </ImportButton>
        </TabHeader>

        <TabsContainer>
          {activeTab === 'json' ? (
            <JsonMappingEditor
              defaultValue={jsonEditorData.current}
              onChange={(newData, newJSONString, jsonInvalidError = null) => {
                // Clear validation error when user starts editing
                if (jsonInvalidError) {
                  setJsonValidationError(jsonInvalidError);
                  setDisableSave(true);
                  jsonEditorData.current = newJSONString;
                  return;
                } else if (jsonValidationError) {
                  setJsonValidationError(null);
                  setDisableSave(false);
                }
                jsonEditorData.current = newJSONString;
                setDataMapping(newData);
              }}
              t={t}
              globalVariables={globalVariables}
              updateGlobalVariables={handleUpdateGlobalVariables}
              relationships={relationships}
            />
          ) : (
            <XPathMappingsEditor
              entries={logicMapping}
              onEntriesChange={setLogicMapping}
              hasSchema={hasSchema}
              dataJson={dataMapping}
              t={t}
              globalVariables={globalVariables}
              updateGlobalVariables={handleUpdateGlobalVariables}
              relationships={relationships}
            />
          )}
        </TabsContainer>
        <div style={{
          padding: '15px 30px',
          borderTop: '1px solid #e8e8e8',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#ffffff'
        }}>
          <div>
            {jsonValidationError && (
              <JsonValidationStatus isValid={false}>
                <Tooltip title={jsonValidationError}>
                  <span style={{ width: 200, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                    {t(trans.INVALID_JSON.key, trans.INVALID_JSON.value)}: {jsonValidationError}
                  </span>
                </Tooltip>
              </JsonValidationStatus>
            )}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
            <SaveButton
              type="secondary"
              // icon=""
              // loading={saving}
              onClick={onClose}
            >
              {"Cancel"}
            </SaveButton>
            <SaveButton
              type="primary"
              icon="save"
              loading={saving}
              onClick={handleSave}
              disabled={disableSave}
              style={{ marginLeft: 10 }}
            >
              {t(trans.SAVE_CONTRACT.key, trans.SAVE_CONTRACT.value)}
            </SaveButton>
          </div>
        </div>
      </RightPanel>
    </EditorContainer>
  );
};

export default withTranslation(NAMESPACES)(ContractEditor);

